import React, { useCallback, useEffect, useState } from 'react';
import { Download, Upload, NotebookPen, CheckCircle2, X, MapPinHouse, Clock, Trash2 } from 'lucide-react';
import '../styles/components/_docsenterprise.scss';
import { ToastContainer, toast } from "react-toastify";
//import Unit from "../domain/entities/unit";
import Tooltip from './tootip/Tootip';
import EnterpriseProvider from '../infra/provider/enterpriseProvider';
import EnterpriseDocuments from '../domain/entities/valueObjects/enterpriseDocuments';
//import constructionCompanyProvider from '../infra/provider/constructionCompanyProvider';
//import enterpriseProvider from '../infra/provider/enterpriseProvider';
import UploadImageRepository from '../infra/repository/uploadImageRepository';
import ObservationsPanel from './observations/ObservationsPanel';
import { Observation } from './observations/ObservationsPanel';

// Funções utilitárias para conversão de datas
const dateToUnixTimestamp = (dateString: string): number => {
    const date = new Date(dateString);
    // Definir para o final do dia (23:59:59)
    date.setHours(23, 59, 59, 999);
    return Math.floor(date.getTime() / 1000);
};

const formatUnixTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('pt-BR');
};

interface Enterprise {
    id: number;
    name: string;
}

interface Document {
    id: number;
    code: string;
    name: string;
    status: 'Não enviado' | 'Aprovado' | 'Reprovado' | 'Não aplicável';
    observation?: string;
    uploaded: boolean;
    fileType?: string;
    path?: string;
    dueDate?: string;
    expiresAt?: number;
    expiresAtFormatted?: string;
}

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (observation: string) => void;
    currentObservation: string;
}
interface AddDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (name: string) => void;
    selectedEnterprise: string;
    onReload: () => Promise<void>;
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ isOpen, onClose, onAdd, selectedEnterprise, onReload }) => {
    const [documentName, setDocumentName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentDoc, setCurrentDoc] = useState<File | null>(null);
    const enterpriseProvider = EnterpriseProvider.getInstance();

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            if (!currentDoc) {
                toast.error("Por favor, faça o upload do documento antes de adicionar.");
                setIsLoading(false);
                return;
            }

            // Primeiro, fazer o upload do arquivo
            const uploadImageRepository = UploadImageRepository.getInstance();
            const documentUrl = await uploadImageRepository.upload(currentDoc);

            // Depois, criar o documento com a URL retornada
            await enterpriseProvider.postEnterpriseDocumentById(
                selectedEnterprise,
                'EX',
                documentName,
                documentUrl
            );

            onAdd(documentName);
            toast.success("Documento adicionado com sucesso!");
            handleCleanDocuments();
            onClose();
            await onReload();
        } catch (error) {
            console.error('Erro ao adicionar documento:', error);
            toast.error("Erro ao adicionar documento.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.png,.jpg,.jpeg';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                setCurrentDoc(file);
                toast.success("Arquivo selecionado com sucesso!");
            }
        };
        input.click();
    };

    const handleCleanDocuments = () => {
        setDocumentName('');
        setCurrentDoc(null);
    }

    return (
        <div className="modal-overlay-unidade">
            <div className="modal-unidade">
                <div className="modal-header-unidade">
                    <h2>Adicionar novo documento</h2>
                    <button className="close-button-unidade" onClick={onClose}>✕</button>
                </div>
                <p>Digite o nome do novo documento.</p>
                <div className="divisor-enterprise"></div>
                <div className='form-group'>
                    <input
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        placeholder="Nome do documento"
                    />
                    <button
                        onClick={() => handleUpload()}
                        className="upload-button-incorp"
                        title="Upload Documento"
                    >
                        <Upload size={10} />
                        <span className="upload-button-text"> Upload do documento</span>
                    </button>
                </div>

                <div className="form-buttons-unidade">
                    <button
                        type="button"
                        className="cancel-button-unidade"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                        disabled={isLoading || !documentName.trim()}
                        onClick={handleSubmit}
                    >
                        {isLoading ? "Salvando..." : "Adicionar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ObservationModal: React.FC<ModalProps> = ({ isOpen, onClose, onSave, currentObservation }) => {
    const [observation, setObservation] = useState(currentObservation);
    const [isLoading, setIsLoading] = useState(false);

    if (!isOpen) return null;

    const handleSave = () => {
        setIsLoading(true);
        onSave(observation);
        setIsLoading(false);
        onClose();
    };

    return (
        <div className="modal-overlay-unidade">
            <div className="modal-unidade">
                <div className="modal-header-unidade">
                    <h2>Adicionar observação:</h2>
                    <button className="close-button-unidade" onClick={onClose}>✕</button>
                </div>
                <p>Adicione uma observação para justificar a invalidação do documento.</p>
                <div className="divisor-enterprise"></div>
                <div className='form-group'>
                    <input
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        placeholder="Digite o motivo da invalidação do documento."
                    />
                </div>

                <div className="form-buttons-unidade">
                    <button
                        type="button"
                        className="cancel-button-unidade"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                        disabled={isLoading}
                        onClick={handleSave}
                    >
                        {isLoading ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

function DocsEnterprises() {
    const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>('tab1');
    const [modalOpen, setModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<EnterpriseDocuments | null>(null);
    const [documents, setDocuments] = useState<{
        preAnalise: EnterpriseDocuments[];
        anexos: EnterpriseDocuments[];
        condicoesHabilit: EnterpriseDocuments[];
        liberacaoParcela: EnterpriseDocuments[];
        recebimentoUltimaParcela: EnterpriseDocuments[];
        outros: EnterpriseDocuments[];
    }>({
        preAnalise: [],
        anexos: [],
        condicoesHabilit: [],
        liberacaoParcela: [],
        recebimentoUltimaParcela: [],
        outros: []
    });
    const enterpriseProvider = EnterpriseProvider.getInstance();
    const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
    const [observations, setObservations] = useState<Observation[]>([]);

    const getEnterprises = useCallback(
        async () => {
            const response = await enterpriseProvider.getAllEnterprises();
            setEnterprises(response as unknown as Enterprise[]);
        },
        [enterpriseProvider]
    );

    const getEnterpriseDocuments = async () => {
        if (selectedEnterprise) {
            try {
                const response = await enterpriseProvider.getEnterpriseDocuments(selectedEnterprise);

                // Ajustando a lógica de filtragem para ser mais precisa
                const preAnalise = response.filter(doc => doc.code.startsWith('PA')).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));
                const anexos = response.filter(doc => doc.code.startsWith('ACF')).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));
                const condicoesHabilit = response.filter(doc => doc.code.startsWith('CH')).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));
                const liberacaoParcela = response.filter(doc => doc.code.startsWith('CLP')).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));
                const recebimentoUltimaParcela = response.filter(doc => doc.code.startsWith('CRUP')).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));
                const outros = response.filter(doc =>
                    !doc.code.startsWith('PA') &&
                    !doc.code.startsWith('ACF') &&
                    !doc.code.startsWith('CH') &&
                    !doc.code.startsWith('CLP') &&
                    !doc.code.startsWith('CRUP')
                ).map(doc => ({
                    ...doc,
                    status: translateAPIStatus(doc.status),
                    uploaded: doc.status !== 'not_sent',
                    expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                    expiresAtFormatted: doc.expiresAtFormatted
                }));

                // Atualizar o estado com os documentos carregados
                setDocuments({
                    preAnalise: preAnalise,
                    anexos: anexos,
                    condicoesHabilit: condicoesHabilit,
                    liberacaoParcela: liberacaoParcela,
                    recebimentoUltimaParcela: recebimentoUltimaParcela,
                    outros: outros
                });
                toast.success('Documentos do empreendimento carregados com sucesso!');
            } catch (error) {
                toast.error('Erro ao buscar documentos do empreendimento:');
            }
        }
    };

    useEffect(() => {
        getEnterprises();
    }, [getEnterprises]);

    const translateAPIStatus = (status: string): EnterpriseDocuments['status'] => {
        switch (status) {
            case 'not_sent':
                return 'Não enviado';
            case 'in_analysis':
                return 'Em análise';
            case 'approved':
                return 'Aprovado';
            case 'reproved':
                return 'Reprovado';
            case 'not_elegible':
                return 'Não aplicável';
            default:
                return 'Não enviado';
        }
    };

    const handleEnterpriseChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        handleCleanDocuments();
        const enterpriseId = e.target.value;
        setSelectedEnterprise(enterpriseId);
        try {
            const response = await enterpriseProvider.getEnterpriseDocuments(enterpriseId);

            const preAnalise = response.filter(doc => doc.code.includes('PA')).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));
            const anexos = response.filter(doc => doc.code.includes('ACF')).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));
            const condicoesHabilit = response.filter(doc => doc.code.includes('CH')).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));
            const liberacaoParcela = response.filter(doc => doc.code.includes('CLP')).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));
            const recebimentoUltimaParcela = response.filter(doc => doc.code.includes('CRUP')).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));
            const outros = response.filter(doc =>
                !doc.code.includes('PA') &&
                !doc.code.includes('ACF') &&
                !doc.code.includes('CH') &&
                !doc.code.includes('CLP') &&
                !doc.code.includes('CRUP')
            ).map(doc => ({
                ...doc,
                status: translateAPIStatus(doc.status),
                uploaded: doc.status !== 'not_sent',
                expiresAt: doc.expiresAt ? Number(doc.expiresAt) : undefined,
                expiresAtFormatted: doc.expiresAtFormatted
            }));

            setDocuments({
                preAnalise: preAnalise,
                anexos: anexos,
                condicoesHabilit: condicoesHabilit,
                liberacaoParcela: liberacaoParcela,
                recebimentoUltimaParcela: recebimentoUltimaParcela,
                outros: outros
            });
            toast.success('Documentos do empreendimento carregados com sucesso!');
        } catch (error) {
            toast.error('Erro ao buscar documentos do empreendimento:');
        }
    };

    const handleCleanDocuments = () => {
        setDocuments({
            preAnalise: [],
            anexos: [],
            condicoesHabilit: [],
            liberacaoParcela: [],
            recebimentoUltimaParcela: [],
            outros: []
        });
    };

    const translateStatus = (status: EnterpriseDocuments['status']) => {
        switch (status) {
            case 'Não enviado':
                return 'not_sent';
            case 'Em análise':
                return 'in_analysis';
            case 'Aprovado':
                return 'approved';
            case 'Reprovado':
                return 'reproved';
            case 'Não aplicável':
                return 'not_elegible';
            default:
                return 'not_sent';
        }
    };

    const handleStatusChange = async (documentId: number, newStatus: EnterpriseDocuments['status']) => {
        const docItem = getActiveDocuments().find(doc => doc.id === documentId);
        if (!docItem) return;

        // Verifica se o documento foi enviado antes de permitir aprovação
        if (newStatus === 'Aprovado' && !docItem.uploaded) {
            toast.error("O documento precisa ser enviado antes de ser aprovado");
            return;
        }

        // Verifica se o documento está em análise antes de permitir aprovação ou reprovação
        if ((newStatus === 'Aprovado' || newStatus === 'Reprovado') && docItem.status !== 'Em análise') {
            toast.error("O documento precisa estar em análise antes de ser aprovado ou reprovado");
            return;
        }

        // Verifica se o documento pode ser marcado como "Em análise" apenas se foi enviado
        if (newStatus === 'Em análise' && !docItem.uploaded) {
            toast.error("O documento precisa ser enviado antes de ser marcado como 'Em análise'");
            return;
        }

        // Se o status for Reprovado ou Não aplicável, abre o modal de observação primeiro
        if (newStatus === 'Reprovado' || newStatus === 'Não aplicável') {
            setSelectedDoc(docItem);
            setModalOpen(true);
            return;
        }

        try {
            // Atualizar o status localmente primeiro
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, status: newStatus }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(updatedDocuments);

            // Enviar para o backend
            const translatedStatus = translateStatus(newStatus);
            console.log('Enviando status:', translatedStatus); // Log para debug
            await enterpriseProvider.putEnterpriseDocumentById(selectedEnterprise, docItem.code, translatedStatus);
            toast.success("Status atualizado com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await getEnterpriseDocuments();
        } catch (error) {
            console.error('Erro ao atualizar status:', error); // Log para debug
            // Em caso de erro, reverter as alterações locais
            const originalDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, status: docItem.status || 'Não enviado' }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(originalDocuments);
            toast.error("Este status não é permitido para este documento. Por favor, verifique as regras de aprovação.");
        }
    };

    const handleDownload = (documentId: number) => {
        // Buscar o documento em todas as categorias
        const allDocuments = [
            ...documents.preAnalise,
            ...documents.anexos,
            ...documents.condicoesHabilit,
            ...documents.liberacaoParcela,
            ...documents.recebimentoUltimaParcela,
            ...documents.outros
        ];

        const docItem = allDocuments.find(doc => doc.id === documentId);

        if (!docItem) {
            toast.error("Documento não encontrado.");
            return;
        }

        if (!docItem.path) {
            toast.error("Não existe um documento para download.");
            return;
        }

        try {
            // Gerar nome do arquivo no formato: code - dataAtual
            const currentDate = new Date();
            const dateString = currentDate.getFullYear().toString() +
                (currentDate.getMonth() + 1).toString().padStart(2, '0') +
                currentDate.getDate().toString().padStart(2, '0') +
                currentDate.getHours().toString().padStart(2, '0') +
                currentDate.getMinutes().toString().padStart(2, '0') +
                currentDate.getSeconds().toString().padStart(2, '0');

            const fileName = `${docItem.code} - ${dateString}`;

            const link = document.createElement('a');
            link.href = docItem.path;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Download iniciado com sucesso!");
        } catch (error) {
            console.error('Erro ao fazer download do documento:', error);
            toast.error("Erro ao fazer download do documento.");
        }
    };

    const handleDownloadOld = (documentId: number) => {
        const docItem = getActiveDocuments().find(doc => doc.id === documentId);
        if (!docItem) return;

        if (!docItem.path) {
            toast.error("Não existe um documento para download.");
            return;
        }

        try {
            // Converter o base64 para blob
            const byteCharacters = atob(docItem.path);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Determinar o tipo MIME baseado na extensão do arquivo
            let mimeType = 'application/pdf';
            const fileName = docItem.name?.toLowerCase() || '';
            if (fileName.endsWith('.png')) {
                mimeType = 'image/png';
            } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
                mimeType = 'image/jpeg';
            }

            const blob = new Blob([byteArray], { type: mimeType });

            // Criar URL do blob e fazer download
            const url = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = docItem.name || 'documento';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("Download iniciado com sucesso!");
        } catch (error) {
            toast.error("Erro ao fazer download do documento.");
        }
    };

    const handleSubmit = async (documentId: number, file: File) => {
        try {
            const docItem = getActiveDocuments().find(doc => doc.id === documentId);
            if (!docItem) return;

            // Verificar se o documento já foi enviado e mostrar confirmação
            if (docItem.path && docItem.uploaded) {
                const confirmReupload = window.confirm(
                    "Este documento já foi enviado anteriormente. Deseja substituí-lo pelo novo arquivo?"
                );
                if (!confirmReupload) {
                    return;
                }
            }

            // Primeiro, fazer o upload do arquivo
            const uploadImageRepository = UploadImageRepository.getInstance();
            const documentUrl = await uploadImageRepository.upload(file);

            if (!documentUrl) {
                toast.error("Erro ao fazer upload do arquivo.");
                return;
            }

            // Depois, atualizar o documento no backend com a URL retornada
            await enterpriseProvider.postEnterpriseDocumentById(
                selectedEnterprise,
                docItem.code,
                file.name,
                documentUrl
            );

            // Atualizar o status para 'in_analysis' (Em análise)
            await enterpriseProvider.putEnterpriseDocumentById(
                selectedEnterprise,
                docItem.code,
                'in_analysis'
            );

            // Atualizar o estado local
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? {
                        ...doc,
                        path: documentUrl,
                        name: file.name,
                        status: 'Em análise',
                        uploaded: true
                    }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(updatedDocuments);

            toast.success("Documento enviado com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await getEnterpriseDocuments();
        } catch (error) {
            console.error('Erro ao enviar documento:', error);
            toast.error("Erro ao enviar documento.");
        }
    };

    const handleUpload = (documentId: number) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.png,.jpg,.jpeg';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                handleSubmit(documentId, file);
            }
        };
        input.click();
    };

    const handleObservationClick = (doc: EnterpriseDocuments) => {
        if (doc.status === 'Reprovado' || doc.status === 'Não aplicável') {
            setSelectedDoc(doc);
            setModalOpen(true);
        }
    };

    const handleAddDocument = async (name: string) => {

        // try {
        //     // Criar o novo documento no backend
        //     await enterpriseProvider.postEnterpriseDocumentById(selectedEnterprise, 'OT', name, null);

        //     // Atualizar o estado local adicionando o novo documento
        //     const newDoc: EnterpriseDocuments = {
        //         id: Date.now(), // ID temporário
        //         code: 'OT',
        //         description: name,
        //         status: 'Enviado', // Definindo status inicial como 'Enviado'
        //         path: ' ', // String com espaço para mostrar o ícone de check
        //         name: name,
        //         observation: '',
        //         uploaded: true
        //     };

        //     const updatedDocuments = [...getActiveDocuments(), newDoc];
        //     updateDocuments(updatedDocuments);

        //     toast.success("Documento adicionado com sucesso!");
        // } catch (error) {
        //     console.error('Erro ao adicionar documento:', error);
        //     toast.error("Erro ao adicionar documento.");
        // }
    };

    const saveObservation = async (observation: string) => {
        if (selectedDoc) {
            try {
                // Determinar o status correto baseado no status atual do documento
                const targetStatus = selectedDoc.status === 'Reprovado' ? 'reproved' : 'not_elegible';

                // Atualizar o estado local
                const activeDocuments = getActiveDocuments();
                const newDocs = activeDocuments.map(doc =>
                    doc.id === selectedDoc.id ? { ...doc, observation } : doc
                );
                updateDocuments(newDocs);

                // Fazer a chamada à API para atualizar o status
                await enterpriseProvider.putEnterpriseDocumentById(
                    selectedEnterprise,
                    selectedDoc.code,
                    targetStatus
                );

                setModalOpen(false);
                toast.success("Status atualizado com sucesso!");
            } catch (error) {
                console.error('Erro ao salvar observação:', error);
                toast.error("Erro ao atualizar status");
                // await getEnterpriseDocuments();
            }
        }
    };

    const handleDueDateChange = async (documentId: number, dueDate: string) => {
        try {
            const docItem = getActiveDocuments().find(doc => doc.id === documentId);
            if (!docItem) return;

            // Validar se a data não é anterior à data atual
            const selectedDate = new Date(dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                toast.error("A data de vencimento não pode ser anterior à data atual");
                return;
            }

            // Atualizar o estado local primeiro
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, dueDate }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(updatedDocuments);

            // Enviar para o backend usando a rota principal
            await enterpriseProvider.putEnterpriseDocumentExpiresAtById(
                selectedEnterprise,
                docItem.code,
                translateStatus(docItem.status),
                docItem.observation,
                dateToUnixTimestamp(dueDate)
            );

            toast.success("Data de vencimento atualizada com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await getEnterpriseDocuments();
        } catch (error) {
            console.error('Erro ao atualizar data de vencimento:', error);
            toast.error("Erro ao atualizar data de vencimento");

            // Reverter as alterações locais em caso de erro
            const docItem = getActiveDocuments().find(doc => doc.id === documentId);
            if (docItem) {
                const revertedDocuments = getActiveDocuments().map(doc =>
                    doc.id === documentId
                        ? { ...doc, dueDate: docItem.dueDate }
                        : doc
                ) as EnterpriseDocuments[];
                updateDocuments(revertedDocuments);
            }
        }
    };

    const handleExpiresAtChange = async (documentId: number, expiresAt: string) => {

        const docItem = getActiveDocuments().find(doc => doc.id === documentId);

        if (!docItem) {
            return;
        }

        if (!expiresAt) {
            return;
        }

        // Guardar o valor original para caso de erro
        const originalExpiresAt = docItem.expiresAt;

        try {
            // Converter a data para timestamp Unix usando a função utilitária
            const expiresAtTimestamp = dateToUnixTimestamp(expiresAt);

            // Validar se a data não é anterior à data atual
            const selectedDate = new Date(expiresAt);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                toast.error("A data de vencimento não pode ser anterior à data atual");
                return;
            }

            // Atualizar o estado local primeiro
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, expiresAt: expiresAtTimestamp }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(updatedDocuments);

            // Fazer a chamada à API
            await enterpriseProvider.putEnterpriseDocumentExpiresAtById(
                selectedEnterprise,
                docItem.code,
                translateStatus(docItem.status),
                docItem.observation,
                expiresAtTimestamp
            );

            toast.success("Data de vencimento atualizada com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await getEnterpriseDocuments();
        } catch (error) {
            toast.error("Erro ao atualizar data de vencimento");
            // Reverter as alterações locais em caso de erro
            setDocuments(prev => {
                const newState = { ...prev };
                switch (activeTab) {
                    case 'tab1': newState.preAnalise = prev.preAnalise.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                    case 'tab2': newState.anexos = prev.anexos.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                    case 'tab3': newState.condicoesHabilit = prev.condicoesHabilit.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                    case 'tab4': newState.liberacaoParcela = prev.liberacaoParcela.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                    case 'tab5': newState.recebimentoUltimaParcela = prev.recebimentoUltimaParcela.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                    case 'tab6': newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: originalExpiresAt } : doc
                    ); break;
                }
                return newState;
            });
        }
    };

    const handleClearDate = async (documentId: number) => {
        const docItem = getActiveDocuments().find(doc => doc.id === documentId);
        if (!docItem) return;

        try {
            // Atualizar o estado local primeiro
            const updatedDocuments = getActiveDocuments().map(doc =>
                doc.id === documentId
                    ? { ...doc, expiresAt: undefined, dueDate: undefined }
                    : doc
            ) as EnterpriseDocuments[];
            updateDocuments(updatedDocuments);

            // Enviar para o backend usando a nova rota DELETE
            await enterpriseProvider.deleteEnterpriseDocumentExpiresAtById(
                documentId.toString()
            );

            toast.success("Data de vencimento removida com sucesso!");

            // Não recarregar documentos para manter as alterações locais
            // await getEnterpriseDocuments();
        } catch (error) {
            console.error('Erro ao remover data de vencimento:', error);
            toast.error("Erro ao remover data de vencimento");
            // Reverter as alterações locais em caso de erro
            setDocuments(prev => {
                const newState = { ...prev };
                switch (activeTab) {
                    case 'tab1': newState.preAnalise = prev.preAnalise.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                    case 'tab2': newState.anexos = prev.anexos.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                    case 'tab3': newState.condicoesHabilit = prev.condicoesHabilit.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                    case 'tab4': newState.liberacaoParcela = prev.liberacaoParcela.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                    case 'tab5': newState.recebimentoUltimaParcela = prev.recebimentoUltimaParcela.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                    case 'tab6': newState.outros = prev.outros.map(doc =>
                        doc.id === documentId ? { ...doc, expiresAt: docItem.expiresAt, dueDate: docItem.dueDate } : doc
                    ); break;
                }
                return newState;
            });
        }
    };

    const getActiveDocuments = () => {
        let activeDocs: EnterpriseDocuments[];
        switch (activeTab) {
            case 'tab1': activeDocs = documents.preAnalise; break;
            case 'tab2': activeDocs = documents.anexos; break;
            case 'tab3': activeDocs = documents.condicoesHabilit; break;
            case 'tab4': activeDocs = documents.liberacaoParcela; break;
            case 'tab5': activeDocs = documents.recebimentoUltimaParcela; break;
            case 'tab6': activeDocs = documents.outros; break;
            default: activeDocs = [];
        }

        return activeDocs;
    };

    const updateDocuments = (newDocs: EnterpriseDocuments[]) => {
        setDocuments(prev => {
            const newState = { ...prev };
            switch (activeTab) {
                case 'tab1': newState.preAnalise = newDocs; break;
                case 'tab2': newState.anexos = newDocs; break;
                case 'tab3': newState.condicoesHabilit = newDocs; break;
                case 'tab4': newState.liberacaoParcela = newDocs; break;
                case 'tab5': newState.recebimentoUltimaParcela = newDocs; break;
                case 'tab6': newState.outros = newDocs; break;
            }
            return newState;
        });
    };

    const renderTable = (documents: EnterpriseDocuments[]) => (
        <>
            {activeTab === 'tab6' && (
                <div className="add-document-button-container" style={{ marginBottom: '1rem', textAlign: 'right' }}>
                    <button
                        onClick={() => setAddModalOpen(true)}
                        className="add-button-plan-viab">
                        + Adicionar documento
                    </button>
                </div>
            )}
            <table className="docs-table-docs">
                <thead>
                    <tr>
                        <th>Situação</th>
                        <th>Nome do Documento</th>
                        <th>Status</th>
                        <th>Vencimento</th>
                        <th>Observação</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {documents.map((doc) => (
                        <tr key={doc.id}>
                            <td>
                                <div className="icon-name-docs">
                                    {doc.status === 'Reprovado' || doc.status === 'Não aplicável' ? (
                                        <X size={20} className="x-icon-docs" />
                                    ) : doc.status === 'Aprovado' ? (
                                        <CheckCircle2 size={20} className="check-icon-docs" />
                                    ) : (
                                        <Clock size={20} className="clock-icon-docs" />
                                    )}
                                </div>
                            </td>
                            <td className="document-name-docs">
                                {doc.description}
                            </td>
                            <td>
                                <select
                                    value={doc.status}
                                    onChange={(e) => handleStatusChange(doc.id, e.target.value as EnterpriseDocuments['status'])}
                                    className="status-select-docs"
                                >
                                    <option value="Não enviado">Não enviado</option>
                                    <option value="Em análise">Em análise</option>
                                    <option value="Aprovado">Aprovado</option>
                                    <option value="Reprovado">Reprovado</option>
                                    <option value="Não aplicável">Não aplicável</option>
                                </select>
                            </td>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="date"
                                        value={doc.expiresAt ? new Date(doc.expiresAt * 1000).toISOString().split('T')[0] : (doc.dueDate || '')}
                                        onChange={(e) => {
                                            // Se temos expiresAt, usar handleExpiresAtChange, senão usar handleDueDateChange
                                            if (doc.expiresAt !== undefined) {
                                                handleExpiresAtChange(doc.id, e.target.value);
                                            } else {
                                                handleDueDateChange(doc.id, e.target.value);
                                            }
                                        }}
                                        className="due-date-input-docs"
                                        min={new Date().toISOString().split('T')[0]}
                                        title={doc.expiresAtFormatted || (doc.expiresAt ? formatUnixTimestamp(doc.expiresAt) : (doc.dueDate || 'Data não definida'))}
                                    />
                                    {(doc.expiresAt || doc.dueDate) && (
                                        <Tooltip text="Limpar">
                                            <button
                                                onClick={() => handleClearDate(doc.id)}
                                                className="clear-date-button"
                                                title="Limpar"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: '#666',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </td>
                            <td className="observation-cell-docs">
                                {doc.observation || '-'}
                            </td>
                            <td className="actions-cell-docs">
                                <Tooltip text={doc.path ? "Upload" : "Upload"}>
                                    <button
                                        onClick={() => handleUpload(doc.id)}
                                        className="action-button"
                                        title={doc.path ? "Upload" : "Upload"}
                                        disabled={false}
                                    >
                                        <Upload size={20} />
                                    </button>
                                </Tooltip>
                                <Tooltip text="Download">
                                    <button
                                        onClick={() => handleDownload(doc.id)}
                                        className="action-button"
                                        title="Download"
                                        disabled={!doc.path}
                                    >
                                        <Download size={20} />
                                    </button>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );

    return (
        <div className="container-docsenterprises-docs">
            <div className="select-container-docs">
                <label htmlFor="enterprise-select" className="select-label-docs">
                    Selecione um empreendimento para visualizar os documentos:
                </label>
                <select
                    id="enterprise-select-docs"
                    className="enterprise-select-docs"
                    value={selectedEnterprise}
                    onChange={(e) => handleEnterpriseChange(e)}
                >
                    <option value="" disabled selected>Selecione um empreendimento</option>
                    {enterprises.map((enterprise) => (
                        <option key={enterprise.id} value={enterprise.id}>
                            {enterprise.name}
                        </option>
                    ))}
                </select>
            </div>

            {
                !selectedEnterprise ? (
                    <div className="empty-state-docs">
                        <MapPinHouse size={150} className="empty-icon-docs" />
                        <p className="empty-text-docs">
                            Selecione um empreendimento para visualizar os resultados.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="tabs-navigation">
                            <button
                                className={`tab-button ${activeTab === 'tab1' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab1')}
                            >
                                Pré-análise
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'tab2' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab2')}
                            >
                                Anexos ao contrato
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'tab3' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab3')}
                            >
                                Documentos de condições de habilitação
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'tab4' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab4')}
                            >
                                Documentos de condições para liberação de parcelas
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'tab5' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab5')}
                            >
                                Documentos de condições para recebimento da ultima parcela
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'tab6' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tab6')}
                            >
                                Outros
                            </button>
                        </div>
                        {renderTable(getActiveDocuments())}
                    </>
                )
            }

            <ObservationModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={saveObservation}
                currentObservation={selectedDoc?.observation || ''}
            />

            <AddDocumentModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onAdd={handleAddDocument}
                selectedEnterprise={selectedEnterprise}
                onReload={getEnterpriseDocuments}
            />

            <ToastContainer />

            {/* Componente de Observações */}
            {selectedEnterprise && (
                <ObservationsPanel
                    entityId={selectedEnterprise}
                    entityType="enterprise"
                    onObservationChange={setObservations}
                />
            )}
        </div >
    );
}

export default DocsEnterprises;